<script lang="ts">
  import { internalLink } from '$lib/utils/internalLink';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { dayOffset, istDay, istTime, moveTouchpointBody, relativeDay, shortDay, plural, touchpointTypeLabel } from '$lib/utils/touchpoints';
  import type { Activity } from '$lib/types';
  import type { ListParams } from '$lib/api/crm';
  import { peopleApi } from '$lib/api/crm';
  import { beginContactCall } from '$lib/utils/pendingCall';

  let { bucket, label, count, params, onOpen }: { bucket: string; label: string; count?: number; params: ListParams; onOpen: (row: Activity, mode: 'log' | 'plan', newConversation?: boolean) => void } = $props();
  const client = useQueryClient();
  let expanded = $state(false), error = $state(''), busy = $state(false), page = $state(1);
  const pinned = $derived(bucket === 'overdue' || bucket === 'today');
  const visible = $derived(expanded || pinned);
  const list = createQuery(() => ({ queryKey: ['agenda-bucket', bucket, params, page], queryFn: () => touchpointsApi.list({ ...params, bucket, page, per_page: 30 }), enabled: visible }));
  const canAct = (row: Activity) => session.isAdmin || row.assignee_user_id === session.me?.user.id;
  const canComplete = (row: Activity) => row.assignee_user_id === session.me?.user.id && (!row.scheduled_at || Date.parse(row.scheduled_at) <= Date.now());
  const sameDayBucket = $derived(bucket === 'today' || bucket === 'tomorrow');

  /** Time column: buckets that are one day show only the clock; others add the day. */
  function when(row: Activity): { primary: string; secondary: string } {
    if (row.status !== 'scheduled') {
      const day = row.next_action_date?.slice(0, 10);
      return { primary: day ? (sameDayBucket ? 'Any time' : shortDay(day)) : 'No date', secondary: day && !sameDayBucket ? relativeDay(day) : 'Follow-up' };
    }
    const at = row.scheduled_at ?? row.due_at;
    if (!at) return { primary: 'No date', secondary: '' };
    const clock = row.scheduled_at ? istTime(at) : `Due ${istTime(at)}`;
    return sameDayBucket ? { primary: clock, secondary: '' } : { primary: shortDay(istDay(at)), secondary: bucket === 'overdue' ? relativeDay(at) : clock };
  }
  async function reschedule(row: Activity) {
    if (busy) return;
    busy = true;
    try {
      await touchpointsApi.reschedule(row.id, moveTouchpointBody(row, dayOffset(1)));
      await client.invalidateQueries();
    } catch (err) {
      error = getErrorMessage(err);
    } finally {
      busy = false;
    }
  }
  async function cancel(row: Activity) {
    if (!await confirmAction(`Cancel “${row.title}”?`)) return;
    try {
      await touchpointsApi.write(`/activities/${row.id}`, { status: 'cancelled' }, 'PUT');
      await client.invalidateQueries();
    } catch (err) {
      error = getErrorMessage(err);
    }
  }
  async function contactAction(row: Activity, whatsapp = false) {
    if (!row.person_id) return;
    const popup = whatsapp ? window.open('about:blank', '_blank') : null;
    try {
      const person = await peopleApi.get(row.person_id);
      const number = whatsapp ? (person.whatsapp || person.phone) : person.phone;
      if (!number) throw new Error('No phone number recorded. Open the contact to add one.');
      beginContactCall(person, whatsapp ? 'whatsapp' : 'call');
      if (whatsapp) {
        if (!popup) throw new Error('Allow pop-ups to open WhatsApp.');
        popup.opener = null;
        popup.location.href = `https://wa.me/${number.replace(/\D/g, '')}`;
      } else {
        window.location.href = `tel:${number}`;
      }
    } catch (err) {
      popup?.close();
      error = getErrorMessage(err);
    }
  }
  async function moveLoaded() {
    const rows = (list.data?.results ?? []).filter(row => row.assignee_user_id === session.me?.user.id);
    if (busy || !rows.length || !await confirmAction(`Move ${plural(rows.length, 'overdue item')} assigned to you to tomorrow?`)) return;
    busy = true;
    const failures: string[] = [];
    for (const row of rows) {
      try { await touchpointsApi.reschedule(row.id, moveTouchpointBody(row, dayOffset(1))); } catch (err) { failures.push(`${row.title}: ${getErrorMessage(err)}`); }
    }
    error = failures.join('; ');
    busy = false;
    await client.invalidateQueries();
  }
</script>

<section class="day-card bucket" class:late={bucket === 'overdue'}>
  <div class="heading">
    {#if pinned}
      <h2 class="day-title">{label}</h2>
    {:else}
      <button type="button" class="toggle" aria-expanded={visible} onclick={() => expanded = !expanded}><span class="caret" class:open={visible}>▸</span><h2 class="day-title">{label}</h2></button>
    {/if}
    <span class="day-chip {bucket === 'overdue' && count ? 'day-chip-danger' : 'day-chip-info'}">{count ?? '—'}</span>
    {#if bucket === 'overdue' && list.data?.results.some(row => row.assignee_user_id === session.me?.user.id)}
      <button type="button" class="day-mini-btn push" disabled={busy} onclick={moveLoaded}>Move mine to tomorrow</button>
    {/if}
  </div>
  {#if visible}
    {#if list.isPending}<p class="tym-loading-state">Loading…</p>
    {:else if list.isError}<p class="tym-error-state" role="alert">{getErrorMessage(list.error)} <button type="button" class="day-mini-btn" onclick={() => list.refetch()}>Retry</button></p>{/if}
    {#if error}<p class="tym-error-state" role="alert">{error}</p>{/if}
    {#each list.data?.results ?? [] as row (row.id)}
      {@const w = when(row)}
      <article class="day-row item">
        <div class="time"><strong>{w.primary}</strong>{#if w.secondary}<span>{w.secondary}</span>{/if}</div>
        <div class="content">
          <div class="who">
            {#if row.person_id}<a class="person" href={`#/people/${row.person_id}`} use:internalLink={{ tab: 'people', id: row.person_id }}>{row.person_name || 'Contact'}</a>{:else}<strong class="person">No contact linked</strong>{/if}
            {#if row.company_name}<span class="day-meta">· {row.company_name}</span>{/if}
          </div>
          <p class="what">{row.status === 'scheduled' ? row.title : row.next_action || row.title}</p>
          <p class="day-meta small">
            <span class="type">{row.status === 'scheduled' ? touchpointTypeLabel(row.type) : 'Follow-up'}</span>
            · {row.status === 'scheduled' ? "Who's going" : 'For'}: {row.assignee_name ?? (row.assignee_user_id ? 'Former team member' : 'Unassigned')}
            {#if row.location}{' · '}{row.location}{/if}
            {#if (row.booked_by_name || row.created_by) && row.status === 'scheduled'}{' · '}Booked by {row.booked_by_name || row.created_by}{/if}
            {#if row.deal_id}{' · '}<a class="day-link" href={`#/deals/${row.deal_id}`} use:internalLink={{ tab: 'deals', id: row.deal_id }}>Deal #{row.deal_id}</a>{/if}
          </p>
        </div>
        <div class="actions">
          {#if canComplete(row)}<button type="button" class="day-mini-btn done" onclick={() => onOpen(row, 'log', true)}>Done</button>{/if}
          {#if canAct(row)}<button type="button" class="day-mini-btn" disabled={busy} onclick={() => reschedule(row)}>Tomorrow</button>{/if}
          {#if row.person_id}
            <button type="button" class="day-mini-btn" onclick={() => contactAction(row)}>Call</button>
            <button type="button" class="day-mini-btn" onclick={() => contactAction(row, true)}>WhatsApp</button>
          {/if}
          <details class="menu">
            <summary class="day-mini-btn" aria-label="Touchpoint actions">⋯</summary>
            <div class="menu-list">
              <button type="button" onclick={() => onOpen(row, row.status === 'scheduled' ? 'plan' : 'log')}>{row.status === 'scheduled' ? 'Pick another time / edit' : 'Edit'}</button>
              {#if canAct(row) && row.status === 'scheduled'}<button type="button" class="danger" onclick={() => cancel(row)}>Cancel plan</button>{/if}
            </div>
          </details>
        </div>
      </article>
    {:else}
      {#if !list.isPending && !list.isError}<p class="empty">{bucket === 'overdue' ? 'Nothing overdue — nice.' : 'Nothing planned.'}</p>{/if}
    {/each}
    {#if list.data && list.data.count > 30}
      <div class="paging">
        <button type="button" class="day-mini-btn" disabled={page === 1} onclick={() => page--}>Previous</button>
        <span class="day-meta">Page {page} of {Math.ceil(list.data.count / 30)}</span>
        <button type="button" class="day-mini-btn" disabled={page * 30 >= list.data.count} onclick={() => page++}>Next</button>
      </div>
    {/if}
  {/if}
</section>

<style>
  .bucket { display: grid; gap: 4px; padding: 16px 20px; }
  .late { border-left: 5px solid var(--tym-danger); }
  .late .day-title { color: var(--tym-danger); }
  .heading { display: flex; align-items: center; gap: 10px; min-height: 36px; }
  .toggle { display: inline-flex; align-items: center; gap: 8px; min-height: 36px; }
  .caret { display: inline-block; color: var(--tym-dark-gray); transition: transform var(--tym-dur-fast) var(--tym-ease-out); }
  .caret.open { transform: rotate(90deg); }
  .push { margin-left: auto; }
  .item { align-items: flex-start; gap: 16px; padding: 12px 6px; }
  .time { flex: none; width: 112px; display: grid; gap: 2px; }
  .time strong { font-family: var(--tym-font-display); font-size: 14px; color: var(--tym-navy); }
  .time span { font-size: 12px; color: var(--tym-dark-gray); }
  .content { flex: 1; min-width: 0; display: grid; gap: 3px; }
  .who { display: flex; flex-wrap: wrap; gap: 4px; align-items: baseline; }
  .person { font-weight: 700; color: var(--tym-navy); }
  a.person:hover { color: var(--tym-teal); text-decoration: underline; }
  .what { font-size: 13.5px; color: var(--tym-heavy-carbon); }
  .small { font-size: 12px; }
  .type { font-weight: 600; color: var(--tym-teal); }
  .actions, .paging { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
  .done { background: var(--tym-lime); border-color: var(--tym-lime); font-weight: 600; }
  .done:hover:not(:disabled) { background: var(--tym-lime-600); }
  .menu { position: relative; }
  .menu summary { list-style: none; cursor: pointer; }
  .menu summary::-webkit-details-marker { display: none; }
  .menu-list { position: absolute; right: 0; top: calc(100% + 6px); z-index: 10; display: grid; min-width: 200px; padding: 6px; border-radius: var(--tym-radius-md); background: var(--tym-white); box-shadow: var(--tym-shadow-3); }
  .menu-list button { text-align: left; padding: 9px 12px; border-radius: var(--tym-radius-sm); font-size: 13px; color: var(--tym-navy); }
  .menu-list button:hover { background: var(--tym-light-carbon); }
  .menu-list .danger { color: var(--tym-danger); }
  .empty { padding: 10px 6px; font-size: 13px; color: var(--tym-dark-gray); }
  .paging { justify-content: center; padding-top: 8px; }
  @media (max-width: 680px) {
    .item { flex-wrap: wrap; gap: 8px; }
    .time { width: 100%; grid-auto-flow: column; justify-content: start; gap: 8px; }
    .actions { width: 100%; }
  }
</style>
